const DB_NAME = "dido-deployment-assets";
const DB_VERSION = 1;
const STORE = "assets";

type CachedAsset = {
  hash: string;
  blob: Blob;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error("indexedDB open failed"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
  });
}

export async function hashBlob(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function readCachedDeploymentAsset(
  remoteUrl: string,
): Promise<CachedAsset | null> {
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const request = tx.objectStore(STORE).get(remoteUrl);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const value = request.result as CachedAsset | undefined;
        resolve(value?.blob && value.hash ? value : null);
      };
    });
  } catch {
    return null;
  }
}

export async function writeCachedDeploymentAsset(
  remoteUrl: string,
  blob: Blob,
  hash: string,
): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const request = tx.objectStore(STORE).put({ hash, blob }, remoteUrl);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  } catch {
    // Cache is best-effort; bundled public assets remain the fallback.
  }
}
